import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Loader2 } from 'lucide-react'
import { fetchVimeoMetadata } from '@/lib/vimeo'
import { toast } from 'sonner'
import type { Database } from '@/lib/supabase/supabase-types'

type Lesson = Database["public"]["Tables"]["lessons"]["Row"]
type LessonInsert = Database["public"]["Tables"]["lessons"]["Insert"]

interface LessonFormProps {
  lesson?: Lesson | null
  courseId: string
  onSubmit: (data: LessonInsert | { id: string } & Partial<Lesson>) => void
  isLoading?: boolean
  onCancel: () => void
  maxPosition: number
}

export default function LessonForm({ lesson, courseId, onSubmit, isLoading, onCancel, maxPosition }: LessonFormProps) {
  const [formData, setFormData] = useState({
    title: lesson?.title || '',
    description: lesson?.description || '',
    vimeo_video_id: lesson?.vimeo_video_id || '',
    duration_seconds: lesson?.duration_seconds || '',
    position: lesson?.position || maxPosition + 1,
    is_required: lesson?.is_required !== false,
  })

  const [vimeoUrl, setVimeoUrl] = useState('')
  const [isFetchingMetadata, setIsFetchingMetadata] = useState(false)

  const handleVimeoUrlSubmit = async () => {
    if (!vimeoUrl) {
      toast.error('Please enter a Vimeo URL')
      return
    }

    setIsFetchingMetadata(true)
    try {
      const metadata = await fetchVimeoMetadata(vimeoUrl)

      if (!metadata) {
        toast.error('Failed to fetch video information. Please check the URL and try again.')
        return
      }

      if (!metadata.videoId) {
        toast.error('Could not extract video ID from the URL')
        return
      }

      // Update form with fetched metadata
      setFormData(prev => ({
        ...prev,
        vimeo_video_id: metadata.videoId || '',
        title: metadata.title || prev.title,
        description: metadata.description || prev.description,
        duration_seconds: metadata.duration || prev.duration_seconds
      }))

      toast.success('Video information loaded! Please review and add duration if needed.')
    } catch (error) {
      console.error('Error fetching Vimeo metadata:', error)
      toast.error('Failed to fetch video information')
    } finally {
      setIsFetchingMetadata(false)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    // Parse duration input - support both seconds and mm:ss format
    let durationSeconds: number | null = null;
    if (formData.duration_seconds) {
      const durationStr = formData.duration_seconds.toString();
      if (durationStr.includes(':')) {
        // Format: mm:ss
        const parts = durationStr.split(':');
        if (parts.length === 2) {
          const minutes = parseInt(parts[0]);
          const seconds = parseInt(parts[1]);
          if (!isNaN(minutes) && !isNaN(seconds)) {
            durationSeconds = minutes * 60 + seconds;
          }
        }
      } else {
        // Just seconds
        const seconds = parseInt(durationStr);
        if (!isNaN(seconds)) {
          durationSeconds = seconds;
        }
      }
    }

    const submitData = {
      ...formData,
      course_id: courseId,
      lesson_type: 'video' as const, // Always set to video
      duration_seconds: durationSeconds,
    }

    if (lesson) {
      // Update existing lesson
      onSubmit({ id: lesson.id, ...submitData })
    } else {
      // Create new lesson
      onSubmit(submitData)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <Label htmlFor="vimeo_url">Vimeo Video URL</Label>
        <div className="flex gap-2">
          <Input
            id="vimeo_url"
            value={vimeoUrl}
            onChange={(e) => setVimeoUrl(e.target.value)}
            placeholder="https://vimeo.com/123456789"
            type="url"
          />
          <Button
            type="button"
            onClick={handleVimeoUrlSubmit}
            disabled={isFetchingMetadata || !vimeoUrl}
          >
            {isFetchingMetadata ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Loading...
              </>
            ) : (
              'Load Video Info'
            )}
          </Button>
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          Paste a Vimeo URL to auto-fill video information
        </p>
      </div>

      {formData.vimeo_video_id && (
        <div className="p-4 bg-muted/50 rounded-lg">
          <p className="text-sm font-medium">Video ID: {formData.vimeo_video_id}</p>
        </div>
      )}

      <div>
        <Label htmlFor="title">Title</Label>
        <Input
          id="title"
          value={formData.title}
          onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
          placeholder="Lesson title"
          required
        />
      </div>

      <div>
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Lesson description"
          rows={3}
        />
      </div>

      <div>
        <Label htmlFor="duration_seconds">Duration</Label>
        <Input
          id="duration_seconds"
          value={formData.duration_seconds}
          onChange={(e) => setFormData(prev => ({ ...prev, duration_seconds: e.target.value }))}
          placeholder="600 (seconds) or 10:00 (mm:ss)"
        />
        <p className="text-sm text-muted-foreground mt-1">
          Enter duration in seconds (e.g., 600) or time format (e.g., 10:00)
        </p>
      </div>

      <div>
        <Label htmlFor="position">Position</Label>
        <Input
          id="position"
          type="number"
          min="1"
          value={formData.position}
          onChange={(e) => setFormData(prev => ({ ...prev, position: parseInt(e.target.value) || 1 }))}
          placeholder="1"
          required
        />
      </div>

      <div className="flex items-center space-x-2">
        <Switch
          id="is_required"
          checked={formData.is_required}
          onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_required: checked }))}
        />
        <Label htmlFor="is_required">Required</Label>
      </div>

      <div className="flex justify-end space-x-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading || !formData.vimeo_video_id}>
          {isLoading ? 'Saving...' : lesson ? 'Update Lesson' : 'Create Lesson'}
        </Button>
      </div>
    </form>
  )
}
